import React, { useState, useEffect, useCallback } from 'react';
import { viloyatUsersAPI as usersAPI } from '../services/api';
import Icon from '../components/Icon';
import { Button, Input, Modal, Toast } from '../components/UI';
import UserForm from '../components/UserForm';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [toast, setToast] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      const { data } = await usersAPI.list(params);
      setUsers(data.results || data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [roleFilter, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const handleCreate = async (formData) => {
    try {
      await usersAPI.create(formData);
      setModalOpen(false);
      loadUsers();
      showToast("Foydalanuvchi yaratildi");
    } catch (e) {
      showToast(e.response?.data?.username?.[0] || "Xatolik yuz berdi", 'error');
    }
  };

  const handleEdit = async (formData) => {
    try {
      await usersAPI.update(editUser.id, formData);
      setEditUser(null);
      setModalOpen(false);
      loadUsers();
      showToast("Ma'lumotlar yangilandi");
    } catch (e) {
      showToast("Xatolik yuz berdi", 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await usersAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadUsers();
      showToast("Foydalanuvchi o'chirildi");
    } catch (e) {
      showToast("Xatolik yuz berdi", 'error');
    }
  };

  const handleToggle = async (u) => {
    try {
      await usersAPI.toggleActive(u.id);
      loadUsers();
      showToast(u.is_active_user ? "Bloklandi" : "Faollashtirildi");
    } catch (e) {
      showToast("Xatolik", 'error');
    }
  };

  const handleResetPw = async () => {
    if (!resetPw) return;
    try {
      await usersAPI.resetPassword(resetModal.id, resetPw);
      setResetModal(null);
      setResetPw('');
      showToast("Parol yangilandi");
    } catch (e) {
      showToast("Xatolik", 'error');
    }
  };

  const getRoleColor = (role) => role === 'tuman_admin' ? { bg: '#dbeafe', color: 'var(--blue)' } : { bg: '#ede9fe', color: 'var(--violet)' };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
            style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }} />
          <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}><Icon name="search" size={15} /></div>
        </div>
        {[
          { val: '', label: 'Barchasi' },
          { val: 'tuman_admin', label: 'Tuman/Shahar adminlar' },
          { val: 'idora', label: 'Idoralar' },
        ].map(r => (
          <button key={r.val} onClick={() => setRoleFilter(r.val)}
            style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: roleFilter === r.val ? 'linear-gradient(135deg, var(--accent), var(--violet))' : '#fff', color: roleFilter === r.val ? '#fff' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, transition: 'all .2s', border: roleFilter === r.val ? 'none' : '1px solid var(--border)' }}>
            {r.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => { setEditUser(null); setModalOpen(true); }} icon="plus">Yangi foydalanuvchi</Button>
        </div>
      </div>

      {/* Table */}
      <div style={{ borderRadius: 16, background: '#fff', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Tuman / Tashkilot', 'Foydalanuvchi', 'Turi', 'Telefon', 'Holat', 'Amallar'].map(h => (
                <th key={h} style={{ padding: '13px 18px', fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Yuklanmoqda...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Foydalanuvchilar topilmadi</td></tr>
            ) : users.map(u => {
              const rc = getRoleColor(u.role);
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '14px 18px', fontSize: 13, fontWeight: 600 }}>
                    {u.tuman || u.tashkilot_nomi || '—'}
                    {u.lavozim && <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 400 }}>{u.lavozim}</div>}
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: rc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: rc.color, flexShrink: 0 }}>
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: "'Fira Code', monospace" }}>@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: rc.bg, color: rc.color }}>
                      {u.role === 'tuman_admin' ? 'Tuman/Shahar admin' : 'Idora'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 18px', fontSize: 12, color: 'var(--text-muted)', fontFamily: "'Fira Code', monospace" }}>{u.phone || '—'}</td>
                  <td style={{ padding: '14px 18px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, background: u.is_active_user ? '#dcfce7' : '#fee2e2', color: u.is_active_user ? 'var(--green)' : 'var(--red)', cursor: 'pointer' }}
                      onClick={() => handleToggle(u)}>
                      {u.is_active_user ? 'Faol' : 'Bloklangan'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button title="Tahrirlash" onClick={() => { setEditUser(u); setModalOpen(true); }} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'var(--accent-light)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="edit" size={14} /></button>
                      <button title="Parol yangilash" onClick={() => { setResetModal(u); setResetPw(''); }} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fef3c7', color: 'var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="key" size={14} /></button>
                      <button title={u.is_active_user ? 'Bloklash' : 'Faollashtirish'} onClick={() => handleToggle(u)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: u.is_active_user ? '#fee2e2' : '#dcfce7', color: u.is_active_user ? 'var(--red)' : 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="power" size={14} /></button>
                      <button title="O'chirish" onClick={() => setDeleteConfirm(u)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fee2e2', color: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="trash" size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen || !!editUser} onClose={() => { setModalOpen(false); setEditUser(null); }} title={editUser ? "Foydalanuvchini tahrirlash" : "Yangi foydalanuvchi"} width={520}>
        <UserForm user={editUser} onSave={editUser ? handleEdit : handleCreate} onClose={() => { setModalOpen(false); setEditUser(null); }} />
      </Modal>

      {/* Delete Confirmation */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="O'chirishni tasdiqlang" width={400}>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text)' }}>{deleteConfirm?.first_name} {deleteConfirm?.last_name}</strong> foydalanuvchisini o'chirmoqchimisiz? Bu amalni qaytarib bo'lmaydi.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={handleDelete} icon="trash">O'chirish</Button>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal open={!!resetModal} onClose={() => setResetModal(null)} title="Parolni yangilash" width={400}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--text)' }}>{resetModal?.first_name} {resetModal?.last_name}</strong> uchun yangi parol:
        </p>
        <Input label="Yangi parol" value={resetPw} onChange={setResetPw} type="password" placeholder="Yangi parol" />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setResetModal(null)}>Bekor qilish</Button>
          <Button onClick={handleResetPw} icon="key" disabled={!resetPw}>Saqlash</Button>
        </div>
      </Modal>
    </div>
  );
}
